import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  signal,
  viewChild,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { assertNonNullable } from '@examdojo/util/assert';
import { isNotNullish } from '@examdojo/util/nullish';
import { BehaviorSubject, delay, distinctUntilChanged, filter, map, merge, Subject, switchMap } from 'rxjs';
import { SwiperContainer } from 'swiper/swiper-element';
import { Swiper } from 'swiper/types';
import { SwiperOptions } from 'swiper/types/swiper-options';

enum ArrowKey {
  Left = 37,
  Right = 39,
  Up = 38,
  Down = 40,
}

@Component({
  selector: 'dojo-swiper',
  imports: [],
  templateUrl: './swiper.component.html',
  styleUrl: './swiper.component.scss',
  host: { class: 'block' },
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class SwiperComponent implements AfterViewInit, OnDestroy {
  private readonly defaultEvents: SwiperOptions['on'] = {
    init: (instance) => {
      this.instance.set(instance);
    },
    transitionStart: () => {
      this.transitionStart$.next();
    },
    transitionEnd: (instance) => {
      const activeIndex = instance.activeIndex;
      this.activeSlideIndex$$.next(activeIndex);
      this.onSlideChange.emit(activeIndex);
      this.transitionEnd$.next();
    },
    activeIndexChange: (swiper) => {
      this.activeIndexChange$$.next(swiper.activeIndex);
    },
    touchEnd: () => {
      this.touchEnd$$.next();
    },
    keyPress: (_instance, keyCode) => {
      const numberKeyCode = parseInt(`${keyCode}`, 10);
      if (this.isNavigationKeyCode(numberKeyCode)) {
        this.keyboardNavigationKeyUp$$.next(numberKeyCode);
      }
    },
  };

  @Input() options: SwiperOptions = {
    slidesPerView: 1,
    spaceBetween: 20,
    pagination: {
      type: 'bullets',
      clickable: true,
    },
    on: this.defaultEvents,
  };

  @Input() paginationTop?: boolean;

  private readonly containerRef = viewChild<ElementRef<SwiperContainer>>('container');
  private readonly container = computed(() => this.containerRef()?.nativeElement);

  readonly instance = signal<Swiper | undefined>(undefined);

  readonly transitionStart$ = new Subject<void>();
  readonly transitionEnd$ = new Subject<void>();
  readonly onSlideChange = new EventEmitter<number>();

  private readonly activeSlideIndex$$ = new BehaviorSubject(0);
  /**
   * Emits after the active slide index changes and the transition has ended
   */
  readonly activeSlideIndex$ = this.activeSlideIndex$$.asObservable().pipe(delay(0), distinctUntilChanged());

  private readonly touchEnd$$ = new Subject<void>();
  readonly touchEnd$ = this.touchEnd$$.asObservable();

  private readonly keyboardNavigationKeyUp$$ = new Subject<ArrowKey>();
  readonly keyboardNavigationKeyUp$ = this.keyboardNavigationKeyUp$$.asObservable();

  private readonly activeIndexChange$$ = new Subject<number>();
  /**
   * Emits as soon as the swiper registers a change of active slide index internally
   */
  readonly activeIndexChange$ = this.activeIndexChange$$.asObservable();

  /**
   * Triggers when swiping with a finger or clicking on a SwiperStepperNextDirective
   */
  readonly slideChangeAfterUserAction$ = toObservable(this.instance).pipe(
    filter(isNotNullish),
    switchMap((instance) =>
      this.activeSlideIndex$.pipe(
        switchMap((slideIndexBefore) => {
          return merge(
            this.touchEnd$.pipe(switchMap(() => this.activeIndexChange$)),
            this.keyboardNavigationKeyUp$.pipe(map(() => instance.activeIndex)),
          ).pipe(
            filter(isNotNullish),
            map((slideIndexAfter) => {
              const isSubmittingLastSlide =
                slideIndexBefore === slideIndexAfter && slideIndexBefore === instance.slides.length - 1;
              return { slideIndexAfter, isSubmittingLastSlide };
            }),
            filter(
              ({ slideIndexAfter, isSubmittingLastSlide }) =>
                slideIndexAfter !== slideIndexBefore || isSubmittingLastSlide,
            ),
            map(
              ({
                slideIndexAfter,
                isSubmittingLastSlide,
              }): { slideIndex: number; direction: 'next' | 'prev' | 'submit' } => {
                return {
                  slideIndex: slideIndexAfter,
                  direction: isSubmittingLastSlide ? 'submit' : slideIndexAfter > slideIndexBefore ? 'next' : 'prev',
                };
              },
            ),
          );
        }),
      ),
    ),
  );

  isFirstSlide() {
    return this.instance()?.isBeginning || this.instance()?.activeIndex === 0;
  }

  isLastSlide() {
    return !!this.instance()?.isEnd;
  }

  ngAfterViewInit() {
    // For some reason the swiper instance gets destroyed if we initialize it immediately
    // The absence of error makes explaining this behaviour difficult
    setTimeout(() => {
      const container = this.container();
      assertNonNullable(container, 'container');
      const options: SwiperOptions = {
        ...(this.options ?? {}),
        on: { ...this.defaultEvents, ...(this.options?.on ?? {}) },
      };
      Object.assign(container, options);
      container.initialize();
    }, 0);
  }

  ngOnDestroy() {
    this.instance()?.destroy();
  }

  updateOption(key: keyof SwiperOptions, value: SwiperOptions[keyof SwiperOptions]) {
    const container = this.container();
    assertNonNullable(container, 'container');
    // @ts-ignore https://swiperjs.com/element#updating-parameters
    container[key] = value;
  }

  private isNavigationKeyCode(kc: number): kc is ArrowKey {
    const isArrowLeft = kc === ArrowKey.Left;
    const isArrowRight = kc === ArrowKey.Right;
    const isArrowUp = kc === ArrowKey.Up;
    const isArrowDown = kc === ArrowKey.Down;

    if (this.options.direction === 'vertical') {
      return isArrowUp || isArrowDown;
    }

    return isArrowLeft || isArrowRight;
  }
}
